import { ethers } from 'ethers';
import { getAlchemyRpcUrl } from '../config/alchemyNetworks';
import { getFromStorage } from '../utils/storage';

/**
 * Estima el costo del gas para una transacción con prioridades EIP-1559
 * Compatible con Dashboard para estimar costos en diferentes redes
 * @param {string} fromAddress - Dirección del remitente
 * @param {string} toAddress - Dirección del destinatario
 * @param {string} [network='homestead'] - Nombre de la red (obtenido de storage si no se proporciona)
 * @returns {Promise<Object>} - Opciones de gas (bajo, medio, alto)
 */
export const estimateGasCost = async (fromAddress, toAddress, network = null) => {
  try {
    if (!ethers.utils.isAddress(fromAddress) || !ethers.utils.isAddress(toAddress)) {
      throw new Error('Direcciones inválidas');
    }

    // Obtener red desde storage si no se proporciona
    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);
    const feeData = await provider.getFeeData();

    // Estimar gasLimit
    const tx = { from: fromAddress, to: toAddress, value: ethers.utils.parseEther('0.001') };
    const gasLimit = await provider.estimateGas(tx).then(gas => gas.mul(12).div(10)); // 20% margen

    // Definir prioridades de gas
    const baseFeePerGas = feeData.lastBaseFeePerGas || ethers.utils.parseUnits('20', 'gwei');
    const gasOptions = {
      low: {
        maxPriorityFeePerGas: ethers.utils.parseUnits('1', 'gwei').toString(),
        maxFeePerGas: baseFeePerGas.add(ethers.utils.parseUnits('1', 'gwei')).mul(11).div(10).toString(),
        gasLimit: gasLimit.toString(),
        estimatedCost: baseFeePerGas.add(ethers.utils.parseUnits('1', 'gwei')).mul(gasLimit).toString(),
      },
      medium: {
        maxPriorityFeePerGas: ethers.utils.parseUnits('2', 'gwei').toString(),
        maxFeePerGas: baseFeePerGas.add(ethers.utils.parseUnits('2', 'gwei')).mul(11).div(10).toString(),
        gasLimit: gasLimit.toString(),
        estimatedCost: baseFeePerGas.add(ethers.utils.parseUnits('2', 'gwei')).mul(gasLimit).toString(),
      },
      high: {
        maxPriorityFeePerGas: ethers.utils.parseUnits('3', 'gwei').toString(),
        maxFeePerGas: baseFeePerGas.add(ethers.utils.parseUnits('3', 'gwei')).mul(11).div(10).toString(),
        gasLimit: gasLimit.toString(),
        estimatedCost: baseFeePerGas.add(ethers.utils.parseUnits('3', 'gwei')).mul(gasLimit).toString(),
      },
    };

    console.log('DEBUG: Opciones de gas estimadas:', { network: effectiveNetwork, gasOptions });
    return gasOptions;
  } catch (error) {
    console.error('Error al estimar costo de gas:', { error, network, fromAddress, toAddress });
    const defaultGasLimit = '21000';
    const defaultMaxFee = ethers.utils.parseUnits('20', 'gwei').toString();
    const defaultPriorityFee = ethers.utils.parseUnits('2', 'gwei').toString();
    const defaultCost = ethers.utils.parseUnits('0.00042', 'ether').toString();

    return {
      low: { maxPriorityFeePerGas: defaultPriorityFee, maxFeePerGas: defaultMaxFee, gasLimit: defaultGasLimit, estimatedCost: defaultCost },
      medium: { maxPriorityFeePerGas: defaultPriorityFee, maxFeePerGas: defaultMaxFee, gasLimit: defaultGasLimit, estimatedCost: defaultCost },
      high: { maxPriorityFeePerGas: defaultPriorityFee, maxFeePerGas: defaultMaxFee, gasLimit: defaultGasLimit, estimatedCost: defaultCost },
    };
  }
};

/**
 * Obtiene un proveedor para la red especificada
 * Compatible con redes soportadas por Dashboard (homestead, matic, world)
 * Nota: Considera almacenar claves de API de Alchemy de forma segura en producción
 * @param {string} [network='homestead'] - Nombre de la red
 * @returns {ethers.providers.JsonRpcProvider} - Proveedor de la red
 */
export const getProvider = (network = null) => {
  try {
    const effectiveNetwork = network || 'homestead';
    const rpcUrl = getAlchemyRpcUrl(effectiveNetwork);
    if (!rpcUrl) {
      console.warn(`No RPC URL para ${effectiveNetwork}, usando proveedor público`);
      const fallbackUrls = {
        homestead: 'https://rpc.sepolia.org',
        matic: 'https://rpc-mainnet.maticvigil.com',
        world: 'https://worldchain-mainnet.g.alchemy.com/public',
      };
      return new ethers.providers.JsonRpcProvider(fallbackUrls[effectiveNetwork] || 'https://rpc.sepolia.org');
    }
    return new ethers.providers.JsonRpcProvider(rpcUrl);
  } catch (err) {
    console.error('Error creando proveedor:', { err, network });
    console.warn('Volviendo al proveedor público de Sepolia');
    return new ethers.providers.JsonRpcProvider('https://rpc.sepolia.org');
  }
};

/**
 * Obtiene el saldo de una dirección en Ether
 * Compatible con Dashboard para mostrar el saldo en la red seleccionada
 * @param {string} address - Dirección Ethereum
 * @param {string} [network='homestead'] - Nombre de la red
 * @returns {Promise<string>} - Saldo en Ether
 */
export const getBalance = async (address, network = null) => {
  try {
    if (!ethers.utils.isAddress(address)) {
      throw new Error('Dirección Ethereum inválida');
    }
    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);
    const balance = await provider.getBalance(address);
    return ethers.utils.formatEther(balance);
  } catch (err) {
    console.error('Error obteniendo saldo:', { err, address, network });
    throw new Error(`Error al obtener el saldo: ${err.message}`);
  }
};

/**
 * Envía una transacción
 * Compatible con Dashboard, espera un wallet desencriptado de Login
 * @param {ethers.Wallet} wallet - Wallet con clave privada
 * @param {string} to - Dirección del destinatario
 * @param {string|number} amount - Cantidad en Ether
 * @param {string} [network='homestead'] - Nombre de la red
 * @param {Object} [txOptions={}] - Opciones de transacción (gas, etc.)
 * @returns {Promise<Object>} - Detalles de la transacción
 */
export const sendTransaction = async (wallet, to, amount, network = null, txOptions = {}) => {
  try {
    if (!ethers.utils.isAddress(to)) {
      throw new Error('Dirección de destinatario inválida');
    }
    if (!wallet || typeof wallet.sendTransaction !== 'function') {
      throw new Error('Wallet inválida');
    }
    if (parseFloat(amount) <= 0) {
      throw new Error('La cantidad debe ser mayor a 0');
    }
    // Validar sesión activa
    const sessionActive = await getFromStorage('sessionActive');
    if (sessionActive !== 'true') {
      throw new Error('Sesión no activa, por favor inicia sesión');
    }

    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);
    const walletWithProvider = wallet.connect(provider);
    const amountInWei = ethers.utils.parseEther(amount.toString());

    // Estimar gasLimit
    const txEstimate = { to, value: amountInWei };
    let gasLimit;
    try {
      gasLimit = await walletWithProvider.estimateGas(txEstimate).then(gas => gas.mul(12).div(10));
    } catch (estimateError) {
      console.warn('DEBUG: Error estimando gas:', estimateError);
      gasLimit = ethers.BigNumber.from(txOptions.gasLimit || '21000');
    }

    // Configurar transacción EIP-1559
    const tx = {
      to,
      value: amountInWei,
      gasLimit,
      maxFeePerGas: ethers.BigNumber.from(txOptions.maxFeePerGas || '20000000000'),
      maxPriorityFeePerGas: ethers.BigNumber.from(txOptions.maxPriorityFeePerGas || '2000000000'),
      type: 2,
    };

    console.log('DEBUG: Enviando transacción:', { network: effectiveNetwork, tx });
    const transactionResponse = await walletWithProvider.sendTransaction(tx);
    console.log('DEBUG: Respuesta de transacción:', transactionResponse);

    const receipt = await transactionResponse.wait();
    console.log('DEBUG: Recibo de transacción:', receipt);

    return {
      hash: transactionResponse.hash,
      from: receipt.from,
      to: receipt.to,
      value: amount,
      blockNumber: receipt.blockNumber,
    };
  } catch (err) {
    console.error('Error enviando transacción:', { err, network, to, amount });
    if (err.message.includes('Sesión no activa')) {
      throw err; // Permitir que Dashboard redirija a /login
    }
    if (err.code === 'UNPREDICTABLE_GAS_LIMIT') {
      throw new Error('No se pudo estimar el gas: posible saldo insuficiente o transacción inválida');
    }
    if (err.code === 'INSUFFICIENT_FUNDS') {
      throw new Error('Fondos insuficientes para la transacción y el gas');
    }
    if (err.message.includes('API')) {
      throw new Error('Error de API: verifica tu clave de Alchemy o los límites de la API');
    }
    throw new Error(`Error al enviar la transacción: ${err.message}`);
  }
};

/**
 * Obtiene el nombre de la red actual
 * Compatible con Dashboard para mostrar la red activa
 * @param {string} [network='homestead'] - Nombre de la red
 * @returns {Promise<string>} - Nombre de la red
 */
export const getCurrentNetwork = async (network = null) => {
  try {
    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);
    const networkInfo = await provider.getNetwork();
    const networkNames = {
      homestead: 'Mainnet',
      matic: 'Polygon',
      world: 'World Chain',
    };
    return networkNames[networkInfo.name] || networkInfo.name.charAt(0).toUpperCase() + networkInfo.name.slice(1);
  } catch (err) {
    console.error('Error obteniendo red:', { err, network });
    throw new Error(`Error al obtener la red: ${err.message}`);
  }
};

/**
 * Obtiene el precio de ETH desde CoinGecko API
 * Compatible con Dashboard para mostrar precios en tiempo real
 * @returns {Promise<Object>} - Datos del precio
 */
export const getEthPrice = async () => {
  try {
    // Usar moneda nativa según la red
    const network = (await getFromStorage('currentNetwork')) || 'homestead';
    const coinId = network === 'matic' ? 'matic-network' : network === 'world' ? 'worldcoin' : 'ethereum';
    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=usd&include_24hr_change=true`,
      { headers: { 'Cache-Control': 'max-age=300' } } // Cachear por 5 minutos
    );
    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return {
      currentPrice: data[coinId].usd,
      priceChange24h: data[coinId].usd_24h_change,
      lastUpdated: Date.now(),
    };
  } catch (error) {
    console.error('Error obteniendo precio desde CoinGecko:', { error, network });
    return {
      currentPrice: network === 'matic' ? 0.5 : network === 'world' ? 2 : 3000,
      priceChange24h: 0,
      lastUpdated: Date.now(),
    };
  }
};

/**
 * Obtiene datos históricos del precio (simplificado)
 * Compatible con Dashboard para análisis de precios
 * @param {number} [days=7] - Días de historial
 * @returns {Promise<number[]>} - Precios históricos
 */
export const getEthPriceHistory = async (days = 7) => {
  try {
    const network = (await getFromStorage('currentNetwork')) || 'homestead';
    const coinId = network === 'matic' ? 'matic-network' : network === 'world' ? 'worldcoin' : 'ethereum';
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=${days}`,
      { headers: { 'Cache-Control': 'max-age=3600' } } // Cachear por 1 hora
    );
    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.prices.map((item) => item[1]);
  } catch (error) {
    console.error('Error obteniendo historial de precios:', { error, days });
    const currentPrice = (await getEthPrice()).currentPrice;
    return Array.from({ length: days }, (_, i) => {
      const variation = Math.random() * 0.1 - 0.05;
      return currentPrice * (1 + variation * (days - i) / days);
    });
  }
};

/**
 * Obtiene transacciones recientes para una dirección
 * Compatible con Dashboard para mostrar historial
 * @param {string} address - Dirección Ethereum
 * @param {string} [network='homestead'] - Nombre de la red
 * @returns {Promise<Object[]>} - Lista de transacciones
 */
export const getRecentTransactions = async (address, network = null) => {
  try {
    if (!ethers.utils.isAddress(address)) {
      throw new Error('Dirección Ethereum inválida');
    }

    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);
    const blockNumber = await provider.getBlockNumber();
    const fromBlock = Math.max(blockNumber - 10000, 0);

    const rpcUrl = getAlchemyRpcUrl(effectiveNetwork);
    if (!rpcUrl) {
      throw new Error(`No se encontró URL RPC para ${effectiveNetwork}`);
    }
    const apiKey = rpcUrl.split('/').pop();
    const baseUrl = {
      homestead: 'https://eth-mainnet.g.alchemy.com/v2/',
      matic: 'https://polygon-mainnet.g.alchemy.com/v2/',
      world: 'https://worldchain-mainnet.g.alchemy.com/v2/',
    }[effectiveNetwork] || 'https://eth-sepolia.g.alchemy.com/v2/';

    const response = await fetch(`${baseUrl}${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'alchemy_getAssetTransfers',
        params: [
          {
            fromBlock: `0x${fromBlock.toString(16)}`,
            toBlock: 'latest',
            fromAddress: address,
            toAddress: address,
            category: ['external'],
            withMetadata: true,
            maxCount: '0x0a',
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`Error API Alchemy: ${data.error.message}`);
    }

    const transfers = data.result.transfers || [];
    const transactions = await Promise.all(
      transfers.map(async (transfer) => {
        const tx = await provider.getTransaction(transfer.hash);
        const receipt = await provider.getTransactionReceipt(transfer.hash);
        const block = await provider.getBlock(transfer.blockNum);

        const isSent = transfer.from.toLowerCase() === address.toLowerCase();
        return {
          hash: transfer.hash,
          type: isSent ? 'Sent ETH' : 'Received ETH',
          value: ethers.utils.formatEther(transfer.value || '0'),
          from: transfer.from,
          to: transfer.to,
          status: receipt.status === 1 ? 'success' : 'failed',
          timestamp: new Date(block.timestamp * 1000),
        };
      })
    );

    return transactions.sort((a, b) => b.timestamp - a.timestamp);
  } catch (err) {
    console.error('Error obteniendo transacciones recientes:', { err, address, network });
    throw new Error(`Error al obtener transacciones: ${err.message}`);
  }
};

/**
 * Obtiene saldos de tokens para una dirección
 * Compatible con Dashboard para mostrar activos
 * @param {string} address - Dirección Ethereum
 * @param {string} [network='homestead'] - Nombre de la red
 * @returns {Promise<Object[]>} - Lista de saldos de tokens
 */
export const getTokenBalances = async (address, network = null) => {
  try {
    if (!ethers.utils.isAddress(address)) {
      throw new Error('Dirección Ethereum inválida');
    }

    const effectiveNetwork = network || (await getFromStorage('currentNetwork')) || 'homestead';
    const provider = getProvider(effectiveNetwork);

    // Lista ampliada de tokens populares por red
    const commonTokens = {
      homestead: [
        {
          address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', // USDT
          name: 'Tether USD',
          symbol: 'USDT',
          decimals: 6,
          logo: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
          coingeckoId: 'tether',
        },
        {
          address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC
          name: 'USD Coin',
          symbol: 'USDC',
          decimals: 6,
          logo: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png',
          coingeckoId: 'usd-coin',
        },
        {
          address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', // DAI
          name: 'Dai Stablecoin',
          symbol: 'DAI',
          decimals: 18,
          logo: 'https://cryptologos.cc/logos/multi-collateral-dai-dai-logo.png',
          coingeckoId: 'dai',
        },
      ],
      matic: [
        {
          address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', // USDC
          name: 'USD Coin',
          symbol: 'USDC',
          decimals: 6,
          logo: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png',
          coingeckoId: 'usd-coin',
        },
        {
          address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', // USDT
          name: 'Tether USD',
          symbol: 'USDT',
          decimals: 6,
          logo: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
          coingeckoId: 'tether',
        },
        {
          address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', // DAI
          name: 'Dai Stablecoin',
          symbol: 'DAI',
          decimals: 18,
          logo: 'https://cryptologos.cc/logos/multi-collateral-dai-dai-logo.png',
          coingeckoId: 'dai',
        },
      ],
      world: [], // World Chain no tiene tokens predefinidos, agregar según soporte
    };

    const tokens = commonTokens[effectiveNetwork] || [];
    const balances = [];

    // Obtener precios de tokens desde CoinGecko
    const tokenIds = tokens.map(token => token.coingeckoId).filter(id => id).join(',');
    let prices = {};
    if (tokenIds) {
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${tokenIds}&vs_currencies=usd`,
          { headers: { 'Cache-Control': 'max-age=300' } }
        );
        if (response.ok) {
          prices = await response.json();
        }
      } catch (priceError) {
        console.warn('Error obteniendo precios de tokens:', priceError);
      }
    }

    for (const token of tokens) {
      const contract = new ethers.Contract(
        token.address,
        ['function balanceOf(address) view returns (uint256)'],
        provider
      );

      const balance = await contract.balanceOf(address);
      const formattedBalance = ethers.utils.formatUnits(balance, token.decimals);

      if (parseFloat(formattedBalance) > 0) {
        balances.push({
          ...token,
          balance: formattedBalance,
          price: prices[token.coingeckoId]?.usd || 1, // Fallback a 1 si no hay precio
        });
      }
    }

    return balances;
  } catch (error) {
    console.error('Error obteniendo saldos de tokens:', { error, address, network });
    return [];
  }
};